import { useEffect, useRef } from 'react';
import { useDispatch, connect, ConnectedProps } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isEmpty, flatMap, isEqual } from 'lodash/fp';
import useEnv from '@shared/useEnv';

import {
  addListener,
  addListenerOnce,
  loadMap,
  createMap,
  getMapOptions,
  latLngBounds,
  getPolygonBounds,
  showMapLogs,
} from '@shared/googleMaps';
import { BuildingSearchResult, Coordinate, SearchListing, StoreState } from '@root/types';
import Button from '@components/shared/V2Button';
import searchPageActions from '@store/actions/listingSearchPage';
import actions from '@store/actions';
import BuildingInfoWindow from '@shared/map/BuildingInfoWindow';
import Markers from '@shared/map/MarkersDeprecated';
import HoveredListingIndicator from '@shared/map/HoveredListingIndicator';
import Drawer from './Drawer';
import MarketBoundaryDrawer from './MarketBoundaryDrawer';
import Zoom from './Zoom';
import s from './Map.module.less';

const startingCoordinates = {
  new_york_city: {
    lat: 40.740038,
    long: -73.99158,
    zoom: 13,
  },
  boston: {
    lat: 42.3481474,
    long: -71.0121329,
    zoom: 9,
  },
};

// TODO : Move this to another file
type Market = {
  abbreviatedRegion: string;
  boundaries: Coordinate[][];
  envelope: Coordinate;
  id: string;
  locality: string;
  name: string;
  slug: string;
};

type PassedProps = {
  buildingSearchResults: BuildingSearchResult[];
  customMapCoordinates: string | null;
  onRedoSearch: (a: any) => null;
  selectedMarkets: Market[];
  onMapReset: () => null;
  userMovedMap: boolean;
  startingMarketSlug: string; // this is also a type
  hoveredListing: null | SearchListing;
  onDrawComplete: (a: any) => null;
};

const mapDispatchToProps = dispatch => ({
  trackBuildingViewedOnMap: () => dispatch(searchPageActions.buildingViewedOnMap()),
  trackMapClickToListing: listing => dispatch(searchPageActions.clickToListingFromMap(listing)),
  trackMapBoundaryDrawingStarted: () => dispatch(searchPageActions.mapBoundaryDrawingStarted()),
  trackMapBoundaryDrawingFinished: () => dispatch(searchPageActions.mapBoundaryDrawingFinished()),
  onMapMove: () => dispatch(actions.mapMoved),
  trackMapDeclustered: () => dispatch(searchPageActions.mapDeclustered()),
  trackMapDoubleClicked: () => dispatch(searchPageActions.mapDoubleClicked()),
});

const mapState = (state: StoreState) => ({
  isDrawing: state.map.isDrawing,
  drawnPolygons: state.map.drawnPolygons,
});

const connector = connect(mapState, mapDispatchToProps);
export type ReduxProps = ConnectedProps<typeof connector>;

export const Map = ({
  buildingSearchResults,
  customMapCoordinates,
  onRedoSearch,
  onMapReset,
  selectedMarkets,
  startingMarketSlug,
  hoveredListing,
  userMovedMap,
  isDrawing,
  onDrawComplete,
  drawnPolygons,
  trackBuildingViewedOnMap,
  trackMapClickToListing,
  trackMapBoundaryDrawingStarted,
  trackMapBoundaryDrawingFinished,
  onMapMove,
  trackMapDeclustered,
  trackMapDoubleClicked,
}: PassedProps & ReduxProps) => {
  // persistant objects
  // TODO : Add types
  // TODO : Figure out which things can be instantiated right away
  const mapRef = useRef<HTMLDivElement>(null);
  const mapObj = useRef() as { current: any | null };
  const drawer = useRef() as { current: any | null };
  const userSelectedMarkets = useRef(selectedMarkets);
  const marketBoundaryDrawer = useRef() as { current: MarketBoundaryDrawer | null };
  const buildingInfoWindow = useRef() as { current: BuildingInfoWindow | null };
  const hoveredListingIndicator = useRef() as { current: any | null };
  const isMapAutoZooming = useRef(false) as { current: boolean | null };
  const markers = useRef() as { current: Markers | null };
  const { t: translate } = useTranslation();
  const { googleMapsKey: key } = useEnv();
  const dispatch = useDispatch();

  const trackers = {
    trackBuildingViewedOnMap,
    trackMapClickToListing,
    trackMapDeclustered,
  };

  const startingPoint =
    startingCoordinates[startingMarketSlug] || startingCoordinates.new_york_city;
  const hasDrawnPolygons = drawnPolygons.length > 0;

  const log = msg => {
    const showLogs = false;

    if (showMapLogs() || showLogs) {
      // eslint-disable-next-line no-console
      console.log(msg);
    }
  };

  const mapMoved = () => {
    if (isMapAutoZooming.current) return;
    onMapMove();
  };

  const closeInfoWindow = () => {
    markers.current?.removeActiveMarker();
    if (buildingInfoWindow.current) {
      buildingInfoWindow.current.close();
    }
  };

  const addMapHandlers = () => {
    addListener(mapObj.current, 'dblclick', () => {
      trackMapDoubleClicked();
    });

    addListener(mapObj.current, 'click', () => {
      log('close infowindow');
      closeInfoWindow();
    });

    addListener(mapObj.current, 'dragend', () => {
      log(`drag end`);
      mapMoved();
    });

    addListener(mapObj.current, 'idle', () => {
      log(`idle, repainting to fix cluster issues`);
      markers.current?.markerClusterer?.repaint();
    });

    addListenerOnce(mapObj.current, 'idle', () => {
      addListener(mapObj.current, 'zoom_changed', () => {
        log(`zoom changed to level ${mapObj.current.getZoom()}`);
        mapMoved();
      });
    });
  };

  const zoomToSearch = () => {
    if (!mapObj.current) return;
    log(`autozooming true`);
    isMapAutoZooming.current = true;

    const marketBounds = flatMap('envelope', userSelectedMarkets.current);

    let bounds = latLngBounds() as google.maps.LatLngBounds | null;
    // this should perhaps be its own function.  something like "setZoom"
    if (drawer.current.drawnPolygons.length > 0) {
      // zoom to drawing
      bounds = getPolygonBounds(drawer.current.drawnPolygons[0]);
    } else if (isEmpty(userSelectedMarkets.current)) {
      // zoom to points
      markers.current?.getMarkers().forEach(marker => {
        const position = marker.getPosition();
        if (position) bounds?.extend(position);
      });
    } else {
      // zoom to market
      marketBounds.forEach(point => bounds?.extend(point));
    }

    mapObj.current?.fitBounds(bounds);

    log(`autozooming false`);
    isMapAutoZooming.current = false;
  };

  // use effects
  useEffect(() => {
    loadMap(key!, () => {
      if (mapObj.current || !mapRef.current) return;
      mapObj.current = createMap(mapRef.current, getMapOptions(startingPoint));

      // dom dependent objects
      hoveredListingIndicator.current = new HoveredListingIndicator({
        mapMarkerText: translate('common:map.markerText'),
      });
      buildingInfoWindow.current = new BuildingInfoWindow({
        translate,
        trackers,
        redesignFlag: false,
      });
      drawer.current = new Drawer({
        map: mapObj.current,
        setUserMovedMap: onMapMove,
        onDrawComplete,
        closeInfoWindow,
        trackers: {
          trackMapBoundaryDrawingStarted,
          trackMapBoundaryDrawingFinished,
        },
      });
      markers.current = new Markers(true, trackers, buildingInfoWindow.current, true);
      marketBoundaryDrawer.current = new MarketBoundaryDrawer();

      // setup
      addMapHandlers();
      Zoom.fitToMapCoordinates(customMapCoordinates, mapObj.current);
    });
  }, [startingMarketSlug, mapRef.current]);

  useEffect(() => {
    if (!mapObj.current) return;
    if (drawnPolygons.length === 0) {
      log('clearing drawn polygons');
      drawer.current.clearDrawnPolygons();
    }
  }, [drawnPolygons]);

  if (!isEqual(selectedMarkets, userSelectedMarkets.current)) {
    // because selectedMarkets is an array, react cannot accurately check
    // when selectedMarkets changes so we force the check and prevent the
    // dependent hooks from running more than necessary
    userSelectedMarkets.current = selectedMarkets;
  }

  useEffect(() => {
    if (!mapObj.current) return;
    log('setting market boundaries');
    marketBoundaryDrawer.current?.createMarketBoundaries(
      userSelectedMarkets.current,
      mapObj.current,
    );
  }, [userSelectedMarkets.current]);

  useEffect(() => {
    if (!mapObj.current) return;
    log('on search results changed');

    // zoom management
    isMapAutoZooming.current = true;

    // autozoom will happen here
    drawer.current.buildPolygons(drawnPolygons, mapObj.current);
    markers.current?.createMarkers(
      buildingSearchResults,
      mapObj.current,
      userSelectedMarkets.current,
    );
    marketBoundaryDrawer.current?.createMarketBoundaries(
      userSelectedMarkets.current,
      mapObj.current,
    );

    // Show the current map without any markers when there are no buildings
    // otherwise the map will be all greyed out
    if (buildingSearchResults.length !== 0) {
      zoomToSearch();
    }
    // now we watch for manual map movement again
    isMapAutoZooming.current = false;
    log(`setting map as loaded`);
  }, [buildingSearchResults, mapObj.current]);

  useEffect(() => {
    if (!mapObj.current) return;

    if (hoveredListing) {
      const hoveredListingBuildingSlug = hoveredListing.buildingSlug;
      const hoveredListingBuilding = buildingSearchResults.filter(
        building => building.slug === hoveredListingBuildingSlug,
      );
      hoveredListingIndicator.current.addHoveredListingMarker(
        hoveredListingBuilding[0],
        mapObj.current,
      );
    } else {
      hoveredListingIndicator.current.clearHoveredListingMarker();
    }
  }, [hoveredListing]);

  // event handlers for buttons
  const handleOnRedoSearchInCustomArea = () => {
    if (!mapObj.current) return;
    dispatch(searchPageActions.mapCustomAreaSearched());
    dispatch(actions.setDrawnPolygons([]));
    const bounds = mapObj.current.getBounds().toUrlValue();
    onRedoSearch(bounds);
  };

  const handleOnMapReset = () => {
    if (!mapObj.current) return;
    dispatch(actions.setDrawnPolygons([]));
    onMapReset();
    dispatch(actions.mapReset);

    zoomToSearch();
    log('on map reset');
  };

  const handleOnDrawClicked = () => {
    if (!mapObj.current) return;
    dispatch(actions.mapDrawStart);
    drawer.current.startDrawing();
  };

  const handleOnCancelClicked = () => {
    if (!mapObj.current) return;
    drawer.current.cancelDrawing();
    onMapReset();
    dispatch(actions.mapDrawEnd);
  };

  return (
    <section className={s.section}>
      <div className={s.mapContainer}>
        <div className={s.buttonContainer}>
          {isDrawing ? (
            <Button
              data-testid="map-draw"
              type="quarternary"
              size="medium"
              className={s.mapDrawButton}
              onClick={handleOnCancelClicked}
            >
              {translate('listingSearch:mapStopDrawing')}
            </Button>
          ) : (
            <Button
              data-testid="map-draw"
              type="quarternary"
              size="medium"
              className={s.mapDrawButton}
              onClick={handleOnDrawClicked}
            >
              {translate('listingSearch:mapDraw')}
            </Button>
          )}
          {userMovedMap && !isDrawing && (
            <Button
              data-testid="redo-search"
              type="quarternary"
              size="medium"
              className={s.redoSearchButton}
              onClick={handleOnRedoSearchInCustomArea}
            >
              {translate('listingSearch:redoSearchInMapArea')}
            </Button>
          )}
          {(userMovedMap || hasDrawnPolygons) && !isDrawing && (
            <Button
              data-testid="map-reset"
              type="quarternary"
              size="medium"
              className={s.mapResetButton}
              onClick={handleOnMapReset}
            >
              {translate('listingSearch:mapReset')}
            </Button>
          )}
        </div>
        <div className={s.map} data-testid="map" ref={mapRef}></div>
      </div>
    </section>
  );
};

export default connector(Map);
